import React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import ReleaseNotesDialog from './ReleaseNotesDialog';
import { getReleaseNotes } from '../../api';
import { logError } from '../../logging/loggingService';

const RELEASE_NOTES_STORAGE_KEY = 'releaseNotesDismissedVersion';

const isReleaseNotesDismissed = (version) => {
    if (!version) {
        return false;
    }
    try {
        return localStorage.getItem(RELEASE_NOTES_STORAGE_KEY) === version;
    } catch {
        return false;
    }
};

const persistReleaseNotesDismissed = (version, dismissed) => {
    if (!version) {
        return;
    }
    try {
        if (dismissed) {
            localStorage.setItem(RELEASE_NOTES_STORAGE_KEY, version);
        } else {
            localStorage.removeItem(RELEASE_NOTES_STORAGE_KEY);
        }
    } catch {}
};

const normalizeEntries = (entries) => {
    if (!Array.isArray(entries)) {
        return [];
    }
    return entries
        .map((entry) => ({
            date: entry?.date ? String(entry.date) : '',
            title: entry?.title ? String(entry.title) : '',
            version: entry?.version ? String(entry.version) : '',
            items: Array.isArray(entry?.items) ? entry.items.filter(Boolean).map(String) : [],
        }))
        .filter((entry) => entry.title || entry.items.length || entry.date || entry.version);
};

const parseEntryDate = (date) => {
    if (!date) {
        return null;
    }
    const parsed = Date.parse(date);
    return Number.isNaN(parsed) ? null : parsed;
};

const selectLatestEntry = (entries) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return null;
    }
    let latest = entries[0];
    let latestTime = parseEntryDate(entries[0]?.date);
    for (let i = 1; i < entries.length; i += 1) {
        const candidate = entries[i];
        const candidateTime = parseEntryDate(candidate?.date);
        if (candidateTime === null) {
            continue;
        }
        if (latestTime === null || candidateTime > latestTime) {
            latest = candidate;
            latestTime = candidateTime;
        }
    }
    return latest;
};

export default function ReleaseNotes({ isAuthenticated, compact = false }) {
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [releaseNotes, setReleaseNotes] = React.useState({ version: '', entries: [] });
    const [suppressOnLogin, setSuppressOnLogin] = React.useState(false);
    const [openedOnLogin, setOpenedOnLogin] = React.useState(false);
    const latestEntry = React.useMemo(
        () => selectLatestEntry(releaseNotes.entries),
        [releaseNotes.entries]
    );
    const visibleEntries = openedOnLogin && latestEntry ? [latestEntry] : releaseNotes.entries;

    const loadReleaseNotes = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await getReleaseNotes();
            const payload = response?.data ?? {};
            const version = typeof payload?.version === 'string' ? payload.version : '';
            const entries = normalizeEntries(payload?.entries);
            const next = { version, entries };
            setReleaseNotes(next);
            return next;
        } catch (err) {
            logError('Release notes fetch failed', err, { component: 'ReleaseNotes' });
            setError('Releasenotes konnten nicht geladen werden.');
            setReleaseNotes({ version: '', entries: [] });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!isAuthenticated) {
            setOpen(false);
            setReleaseNotes({ version: '', entries: [] });
            setSuppressOnLogin(false);
            setOpenedOnLogin(false);
            return;
        }

        let active = true;
        loadReleaseNotes().then((notes) => {
            if (!active || !notes) {
                return;
            }
            const dismissed = isReleaseNotesDismissed(notes.version);
            setSuppressOnLogin(dismissed);
            if (!dismissed && notes.entries.length > 0) {
                setOpenedOnLogin(true);
                setOpen(true);
            } else {
                setOpenedOnLogin(false);
            }
        });

        return () => {
            active = false;
        };
    }, [isAuthenticated, loadReleaseNotes]);

    const handleOpen = async () => {
        let current = releaseNotes;
        if (!current.version && !current.entries.length && !loading) {
            const next = await loadReleaseNotes();
            if (next) {
                current = next;
            }
        }
        if (!open) {
            setOpenedOnLogin(false);
        }
        setSuppressOnLogin(isReleaseNotesDismissed(current.version));
        setOpen(true);
    };

    const handleClose = () => {
        persistReleaseNotesDismissed(releaseNotes.version, suppressOnLogin);
        setOpen(false);
        setOpenedOnLogin(false);
    };

    if (!isAuthenticated) {
        return null;
    }

    const trigger = compact ? (
        <Tooltip title="Releasenotes">
            <IconButton color="inherit" onClick={handleOpen} aria-label="Releasenotes">
                <NewReleasesOutlinedIcon />
            </IconButton>
        </Tooltip>
    ) : (
        <Button
            color="inherit"
            startIcon={<NewReleasesOutlinedIcon />}
            onClick={handleOpen}
        >
            Releasenotes
        </Button>
    );

    return (
        <>
            {trigger}
            <ReleaseNotesDialog
                open={open}
                onClose={handleClose}
                entries={visibleEntries}
                loading={loading}
                error={error}
                suppressOnLogin={suppressOnLogin}
                onSuppressChange={(event) => setSuppressOnLogin(event.target.checked)}
                canDismiss={Boolean(releaseNotes.version) && openedOnLogin}
            />
        </>
    );
}

